import { filterSessionsByCourse } from '../../utils/sessionUtils';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api/api';
import { Eye, Search, User, Mail, Phone, Trash2, Download, FileText } from 'lucide-react';
import Pagination from '@/components/Pagination';
import { exportTableToPDF } from '@/utils/pdfExport';
import * as XLSX from 'xlsx';

const AdmissionReportList = () => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCourse, setFilterCourse] = useState('all');
  const [filterSession, setFilterSession] = useState('all');
  const [filterEntryType, setFilterEntryType] = useState('all');
  const [deleting, setDeleting] = useState(false);
  const [collegeInfo, setCollegeInfo] = useState(null);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    fetchStudents();
    fetchCourses();
    fetchSessions();
    api.get('/tenants/my-college/settings').then(r => setCollegeInfo(r.data)).catch(() => {});
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    filterStudentList();
  }, [searchTerm, filterCourse, filterSession, filterEntryType, students]);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await api.get('/students/', {
        params: {
          include_all: true,
          page: currentPage,
          limit: itemsPerPage
        }
      });
      
      // Handle both old (array) and new (paginated) response
      let studentsData = [];
      let total = 0;
      
      if (Array.isArray(response.data)) {
        studentsData = response.data;
        total = response.data.length;
      } else {
        studentsData = response.data?.data || [];
        total = response.data?.total || 0;
      }
      
      // Show all admitted students.
      // - Old admissions (no is_approved flag) → show unless explicitly withdrawn/cancelled
      // - New admissions → respect is_approved=true
      // - Hide pending/draft applications that aren't approved yet
      const EXCLUDED_STATUSES = new Set([
        'Pending', 'pending',
        'Rejected', 'rejected',
        'Withdrawn', 'withdrawn',
        'Cancelled', 'cancelled', 'Canceled', 'canceled',
        'TC Issued', 'tc_issued', 'TC_Issued',
        'Passout', 'passout', 'Passed Out',
        'Draft', 'draft',
      ]);
      const approvedStudents = studentsData.filter(student => {
        // Always include explicitly approved
        if (student.is_approved === true) return true;
        // For older records without the flag — include unless excluded status
        if (student.status && EXCLUDED_STATUSES.has(student.status)) return false;
        return true;
      });
      setStudents(approvedStudents);
      setTotalItems(total);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const fetchCourses = async () => {
    try {
      const response = await api.get('/master-settings/courses');
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await api.get('/master-settings/sessions');
      setSessions(response.data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const filterStudentList = () => {
    let filtered = [...students];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(student =>
        student.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.admission_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.phone?.includes(searchTerm)
      );
    }

    // Course filter
    if (filterCourse !== 'all') {
      filtered = filtered.filter(student => student.course_id === filterCourse);
    }

    // Session filter
    if (filterSession !== 'all') {
      filtered = filtered.filter(student => student.session === filterSession);
    }

    // Entry type filter
    if (filterEntryType !== 'all') {
      if (filterEntryType === 'regular') {
        filtered = filtered.filter(student => !student.is_lateral_entry);
      } else if (filterEntryType === 'lateral') {
        filtered = filtered.filter(student => student.is_lateral_entry);
      }
    }

    setFilteredStudents(filtered);
  };

  const handleViewReport = (studentId) => {
    navigate(`/admission/report/${studentId}`);
  };

  const handleDeleteStudent = async (student) => {
    const confirmMsg = `Are you sure you want to delete student "${student.full_name}" (${student.admission_number})?\n\nThis will also delete all related fee records and cannot be undone!`;
    
    if (!window.confirm(confirmMsg)) return;
    
    // Get reason for deletion
    const reason = window.prompt('Please enter the reason for deletion:');
    if (!reason || !reason.trim()) {
      alert('Deletion cancelled. Reason is required.');
      return;
    }
    
    setDeleting(true);
    try {
      await api.delete(`/students/${student.id}?reason=${encodeURIComponent(reason.trim())}`);
      alert(`Student "${student.full_name}" deleted successfully!`);
      fetchStudents(); // Refresh list
    } catch (error) {
      console.error('Error deleting student:', error);
      alert(error.response?.data?.detail || 'Failed to delete student. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const getCourseName = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course ? course.name : 'N/A';
  };

  const getExportData = () => {
    return filteredStudents.map((s, i) => ({
      'Sl No': i + 1,
      'Student Name': s.full_name || 'N/A',
      'Admission No': s.admission_number || 'N/A',
      'Email': s.email || 'N/A',
      'Phone': s.phone || 'N/A',
      'Course': getCourseName(s.course_id),
      'Session': s.session || 'N/A',
      'Course Fee': s.total_course_fee || 0,
      'Net Course Fee': (s.total_course_fee || 0) - (s.total_discount || s.discount_amount || 0) - (s.total_scholarship || s.scholarship_amount || 0) - (s.admission_amount || s.admission_fee || 0) - (s.seat_booking_amount || s.booking_amount || 0),
      'Entry Type': s.is_lateral_entry ? 'Lateral' : 'Regular',
      'Status': s.is_approved ? 'Approved' : s.is_blocked ? 'Blocked' : 'Pending'
    }));
  };

  const handleExportExcel = () => {
    const data = getExportData();
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Admission Report');
    XLSX.writeFile(wb, `Admission_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleExportPDF = () => {
    const columns = [
      { header: 'Sl No', dataKey: 'Sl No' },
      { header: 'Student Name', dataKey: 'Student Name' },
      { header: 'Admission No', dataKey: 'Admission No' },
      { header: 'Course', dataKey: 'Course' },
      { header: 'Course Fee', dataKey: 'Course Fee' },
      { header: 'Net Course Fee', dataKey: 'Net Course Fee' },
      { header: 'Entry Type', dataKey: 'Entry Type' },
      { header: 'Status', dataKey: 'Status' }
    ];
    const data = getExportData();
    exportTableToPDF('Admission Report', columns, data, `Admission_Report_${new Date().toISOString().slice(0, 10)}.pdf`, collegeInfo);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admission Reports</h1>
          <p className="text-sm text-gray-500 mt-1">View detailed admission reports for approved students</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExportExcel}
            disabled={filteredStudents.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm disabled:opacity-50"
            data-testid="export-excel-btn"
          >
            <Download className="h-4 w-4" />
            Excel
          </button>
          <button
            onClick={handleExportPDF}
            disabled={filteredStudents.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm disabled:opacity-50"
            data-testid="export-pdf-btn"
          >
            <FileText className="h-4 w-4" />
            PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, admission no, email, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Course Filter */}
          <div>
            <select
              value={filterCourse}
              onChange={(e) => setFilterCourse(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Courses</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>
          </div>

          {/* Session Filter */}
          <div>
            <select
              value={filterSession}
              onChange={(e) => setFilterSession(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Sessions</option>
              {filterSessionsByCourse(sessions, filterCourse === 'all' ? '' : filterCourse).map((session) => (
                <option key={session.id} value={session.name}>{session.name}</option>
              ))}
            </select>
          </div>

          {/* Entry Type Filter */}
          <div>
            <select
              value={filterEntryType}
              onChange={(e) => setFilterEntryType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Entry Types</option>
              <option value="regular">Regular Entry</option>
              <option value="lateral">Lateral Entry</option>
            </select>
          </div>
        </div>

        {/* Results Count */}
        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredStudents.length} of {students.length} students
        </div>
      </div>

      {/* Students Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student Details</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admission No.</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course Fee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Course Fee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entry Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">Loading...</td>
                </tr>
              ) : filteredStudents.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">No students found</td>
                </tr>
              ) : (
                filteredStudents.map((student) => (
                  <tr key={student.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <User className="h-5 w-5 text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{student.full_name}</div>
                          <div className="text-xs text-gray-500 flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {student.email}
                          </div>
                          <div className="text-xs text-gray-500 flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {student.phone}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{student.admission_number}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{getCourseName(student.course_id)}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      ₹{(student.total_course_fee || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-blue-700">
                      ₹{((student.total_course_fee || 0) - (student.total_discount || student.discount_amount || 0) - (student.total_scholarship || student.scholarship_amount || 0) - (student.admission_amount || student.admission_fee || 0) - (student.seat_booking_amount || student.booking_amount || 0)).toLocaleString('en-IN')}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        student.is_lateral_entry 
                          ? 'bg-orange-100 text-orange-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {student.is_lateral_entry ? 'Lateral' : 'Regular'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        student.is_approved === true ? 'bg-green-100 text-green-800' :
                        student.is_blocked === true ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {student.is_approved === true ? 'Approved' : student.is_blocked === true ? 'Blocked' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleViewReport(student.id)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                          title="View Report"
                        >
                          <Eye className="h-4 w-4" />
                          View
                        </button>
                        <button
                          onClick={() => handleDeleteStudent(student)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                          title="Delete Student"
                          disabled={deleting}
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalItems / itemsPerPage)}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </div>
    </div>
  );
};

export default AdmissionReportList;
